import { useState, useCallback, useEffect, useRef } from 'react';
import {
  documentService,
  UploadResponse,
  ProcessingStatusResponse,
  ProcessingResult
} from '../services/document';
import { consentService, ConsentState } from '../services/consentService';
import { useAuth } from '../../../contexts/AuthContext';
import { apiService } from '../../../services/api';

export interface ContractorProfileState {
  isUploading: boolean;
  uploadError: string | null;
  uploadedDocument: UploadResponse | null;

  isProcessing: boolean;
  processingStatus: ProcessingStatusResponse | null;
  processingError: string | null;
  processingResult: ProcessingResult | null;

  pdfBlob: Blob | null;
  originalFile: File | null;

  consent: ConsentState | null;
  isLoadingConsent: boolean;
  consentError: string | null;

  currentStep: 'upload' | 'processing' | 'completed' | 'error';
}

const INITIAL_CONSENT: ConsentState = {
  consented: false,
  consented_at: null,
  withdrawn_at: null,
  sharepoint_url: null
};

export const useContractorProfile = () => {
  const { getAccessToken } = useAuth();
  const [state, setState] = useState<ContractorProfileState>({
    isUploading: false,
    uploadError: null,
    uploadedDocument: null,
    isProcessing: false,
    processingStatus: null,
    processingError: null,
    processingResult: null,
    pdfBlob: null,
    originalFile: null,
    consent: null,
    isLoadingConsent: false,
    consentError: null,
    currentStep: 'upload'
  });

  const statusPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  const loadConsent = useCallback(async () => {
    setState(prev => ({ ...prev, isLoadingConsent: true, consentError: null }));
    try {
      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      }
      const consent = await consentService.getConsent();
      setState(prev => ({ ...prev, consent, isLoadingConsent: false }));
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setState(prev => ({ ...prev, consent: INITIAL_CONSENT, isLoadingConsent: false }));
      } else {
        setState(prev => ({
          ...prev,
          consent: INITIAL_CONSENT,
          isLoadingConsent: false,
          consentError: 'Failed to load consent status'
        }));
      }
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadConsent();
  }, [loadConsent]);

  const giveConsent = useCallback(async (pdfFile: File, filename: string, profilePhoto: string | null) => {
    setState(prev => ({ ...prev, isLoadingConsent: true, consentError: null }));
    try {
      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      }
      const consent = await consentService.giveConsent(pdfFile, filename, profilePhoto);
      setState(prev => ({ ...prev, consent, isLoadingConsent: false }));
      return consent;
    } catch (error: any) {
      const message = error?.response?.data?.detail || error?.message || 'Failed to submit consent';
      setState(prev => ({ ...prev, isLoadingConsent: false, consentError: message }));
      throw error;
    }
  }, [getAccessToken]);

  const withdrawConsent = useCallback(async () => {
    setState(prev => ({ ...prev, isLoadingConsent: true, consentError: null }));
    try {
      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      }
      const consent = await consentService.withdrawConsent();
      setState(prev => ({ ...prev, consent, isLoadingConsent: false }));
      return consent;
    } catch (error: any) {
      const message = error?.response?.data?.detail || error?.message || 'Failed to withdraw consent';
      setState(prev => ({ ...prev, isLoadingConsent: false, consentError: message }));
      throw error;
    }
  }, [getAccessToken]);

  const pollStatus = useCallback((documentId: string) => {
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
    }

    const interval = setInterval(async () => {
      try {
        const token = await getAccessTokenRef.current();
        if (token) {
          apiService.setAuthToken(token);
        }

        const statusResponse = await documentService.getProcessingStatus(documentId);

        setState(prev => ({ ...prev, processingStatus: statusResponse }));

        if (statusResponse.status === 'completed') {
          clearInterval(interval);
          statusPollingIntervalRef.current = null;

          try {
            const token = await getAccessTokenRef.current();
            if (token) {
              apiService.setAuthToken(token);
            }
            const result = await documentService.getProcessingResult(documentId);

            setState(prev => ({
              ...prev,
              isProcessing: false,
              processingResult: result,
              currentStep: 'completed'
            }));
          } catch (resultError: any) {
            setState(prev => ({
              ...prev,
              isProcessing: false,
              processingError: 'Failed to fetch processing result',
              currentStep: 'error'
            }));
          }
        } else if (statusResponse.status === 'failed') {
          clearInterval(interval);
          statusPollingIntervalRef.current = null;
          setState(prev => ({
            ...prev,
            isProcessing: false,
            processingError: statusResponse.error_message || 'Processing failed',
            currentStep: 'error'
          }));
        }
      } catch (error: any) {
        // Don't stop polling on transient errors
      }
    }, 2000);

    statusPollingIntervalRef.current = interval;
  }, []);

  const startProcessing = useCallback(async (documentId: string) => {
    try {
      setState(prev => ({
        ...prev,
        isProcessing: true,
        processingError: null,
        currentStep: 'processing'
      }));

      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      }

      await documentService.startProcessing(documentId);
      pollStatus(documentId);
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        processingError: error.response?.data?.detail || error.message || 'Failed to start processing',
        currentStep: 'error'
      }));
    }
  }, [pollStatus, getAccessToken]);

  const uploadFile = useCallback(async (file: File) => {
    try {
      const validation = documentService.validateFile(file);
      if (!validation.valid) {
        setState(prev => ({ ...prev, uploadError: validation.error || 'Invalid file', currentStep: 'error' }));
        return;
      }

      const isPDF = file.name.toLowerCase().endsWith('.pdf');
      if (isPDF) {
        setState(prev => ({ ...prev, pdfBlob: file, originalFile: file }));
      }

      setState(prev => ({
        ...prev,
        isUploading: true,
        uploadError: null,
        originalFile: file,
        currentStep: 'upload'
      }));

      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      } else {
        throw new Error('Failed to get authentication token');
      }

      const uploadResponse = await documentService.uploadDocument(file);

      if (uploadResponse.needs_conversion && uploadResponse.pdf_blob) {
        const pdfBytes = atob(uploadResponse.pdf_blob);
        const pdfArray = new Uint8Array(pdfBytes.length);
        for (let i = 0; i < pdfBytes.length; i++) {
          pdfArray[i] = pdfBytes.charCodeAt(i);
        }
        const pdfBlobToStore = new Blob([pdfArray], { type: 'application/pdf' });
        setState(prev => ({ ...prev, pdfBlob: pdfBlobToStore }));
      }

      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadedDocument: uploadResponse,
        currentStep: 'upload'
      }));

      startProcessing(uploadResponse.document_id);
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadError: error.response?.data?.detail || error.message || 'Upload failed',
        currentStep: 'error'
      }));
    }
  }, [startProcessing, getAccessToken]);

  const retryProcessing = useCallback(async (documentId: string) => {
    try {
      setState(prev => ({
        ...prev,
        isProcessing: true,
        processingError: null,
        processingResult: null,
        currentStep: 'processing'
      }));

      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      }

      await documentService.retryProcessing(documentId);
      pollStatus(documentId);
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        processingError: error.response?.data?.detail || error.message || 'Failed to retry processing',
        currentStep: 'error'
      }));
    }
  }, [pollStatus, getAccessToken]);

  const resetState = useCallback(() => {
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
      statusPollingIntervalRef.current = null;
    }

    setState(prev => ({
      ...prev,
      isUploading: false,
      uploadError: null,
      uploadedDocument: null,
      isProcessing: false,
      processingStatus: null,
      processingError: null,
      processingResult: null,
      pdfBlob: null,
      originalFile: null,
      currentStep: 'upload'
    }));
  }, []);

  useEffect(() => {
    return () => {
      if (statusPollingIntervalRef.current) {
        clearInterval(statusPollingIntervalRef.current);
      }
    };
  }, []);

  return {
    ...state,
    uploadFile,
    startProcessing,
    retryProcessing,
    resetState,
    giveConsent,
    withdrawConsent,
    loadConsent
  };
};
